// Change the size of a placed fitting (owner, 2026-10-19): "make the fittings size variable so I can set a 50mm T and
// change it to a 150mm T and the pipes will also be that size".
//
// The fitting keeps its id, so every pipe that is attached to it stays attached. It is redrawn at the new size, turned
// as it was, with its CENTRE where it was: the point where the axes of its ends meet (bend, tee) or the middle between
// its ends (reducer). So each end stays on its own axis, and a pipe on an end stays in line; only its length changes.
// The pipes on its ends take the size of their end, and so do pipes joined to those pipes (a run), up to the next
// fitting or pump. Other fittings are not changed.
//
// A fitting has the same parts at every size (tests/resizeFitting.test.ts checks it), so the redraw is a set of patches
// on the parts it already has. A fitting that was ungrouped or edited has other parts and is refused.
import type { Vec } from "../core/geometry";
import type { Doc, Entity } from "../doc/entities";
import { applyCommand, type Command } from "../doc/commands";
import { makeFittingItem, type FittingInfo, type FittingKind } from "../library/fittings";
import { pipeMetaOf, setPipePatches, type FittingPortData, type Patch } from "./api";
import { rawPorts } from "./carry";

/** Sizes offered for fittings, mm: the library sizes and the DN sizes of the Davey pump connections. */
export const FITTING_SIZE_CHOICES = [32, 40, 50, 65, 80, 100, 125, 150, 200, 250] as const;

const KINDS = new Set<FittingKind>(["bend90", "bend45", "tee", "reducer", "teeup", "teedown"]);
/** A tee of any kind: its second size is the branch, never larger than the run. */
export const isTeeKind = (k: FittingKind): boolean => k === "tee" || k === "teeup" || k === "teedown";

/** meta.fitting of a group, when it is a bend, tee or reducer. */
export function fittingInfoOf(entity: Entity | undefined): FittingInfo | null {
  const f = entity?.type === "group" ? (entity.meta?.fitting as FittingInfo | undefined) : undefined;
  if (!f || !KINDS.has(f.kind) || typeof f.size !== "number" || !(f.size > 0)) return null;
  return f;
}

const ang = (v: Vec): number => Math.atan2(v.y, v.x);
const rot = (v: Vec, a: number): Vec => ({ x: v.x * Math.cos(a) - v.y * Math.sin(a), y: v.x * Math.sin(a) + v.y * Math.cos(a) });
const sig = (e: Entity): string => e.type + (e.type === "polyline" ? `:${e.points.length}:${e.closed ? 1 : 0}` : "");

/** Where two lines p + t·d meet; null when they are parallel. */
function meet(p: Vec, d: Vec, q: Vec, e: Vec): Vec | null {
  const den = d.x * e.y - d.y * e.x;
  if (Math.abs(den) < 1e-12) return null;
  const t = ((q.x - p.x) * e.y - (q.y - p.y) * e.x) / den;
  return { x: p.x + d.x * t, y: p.y + d.y * t };
}

/** The centre of a fitting from its ends: axes meeting (bend, tee), else the middle of the first two ends. */
function centreOf(kind: FittingKind, ports: FittingPortData[]): Vec {
  const [a, b, c] = ports;
  const mid = { x: (a!.p.x + b!.p.x) / 2, y: (a!.p.y + b!.p.y) / 2 };
  if (kind === "tee" && c) return meet(a!.p, a!.dir, c.p, c.dir) ?? mid;
  if (kind === "bend90" || kind === "bend45") return meet(a!.p, a!.dir, b!.p, b!.dir) ?? mid;
  return mid;
}

/** The ends of a placed fitting. A fitting from before 3.8.0 has no ends stored: they are rebuilt from its turning point and turn. */
function placedPorts(g: Entity, info: FittingInfo): FittingPortData[] | null {
  const stored = rawPorts(g);
  if (stored) return stored;
  const pivot = g.meta?.pivot as Vec | undefined;
  if (!pivot) return null;
  const item = makeFittingItem(info.kind, info.size, info.size2);
  const ig = item.entities.find((e) => e.type === "group")!;
  const ipivot = ig.meta!.pivot as Vec;
  const turn = (((g.meta?.rot as number | undefined) ?? 0) * Math.PI) / 180;
  return (ig.meta!.ports as FittingPortData[]).map((pt) => {
    const r = rot({ x: pt.p.x - ipivot.x, y: pt.p.y - ipivot.y }, turn);
    return { p: { x: pivot.x + r.x, y: pivot.y + r.y }, dir: rot(pt.dir, turn), size: pt.size };
  });
}

/** The sizes a request ends up with: a tee branch never larger than the run; a reducer's small end smaller than its large end. */
export function normalSizes(kind: FittingKind, size: number, size2: number | undefined, old: FittingInfo): { size: number; size2?: number } | string {
  if (!(size > 0)) return "Choose a size.";
  if (isTeeKind(kind)) {
    const wasEqual = old.size2 === undefined || old.size2 === old.size;
    const b = size2 ?? (wasEqual ? size : (old.size2 as number));
    return { size, size2: Math.min(b, size) };
  }
  if (kind === "reducer") {
    const s2 = size2 ?? old.size2 ?? size;
    if (!(s2 < size)) return "A reducer needs a small end that is smaller than its large end.";
    return { size, size2: s2 };
  }
  return { size };
}

/** Pipes joined end to end with `start` (both ways), not crossing a fitting or a pump. */
export function runFrom(doc: Doc, start: string): string[] {
  const seen = new Set<string>([start]);
  const queue = [start];
  while (queue.length) {
    const id = queue.shift()!;
    const pm = pipeMetaOf(doc.entities[id]);
    if (!pm) continue;
    for (const a of [pm.a, pm.b]) {
      if (a.kind === "port" && !seen.has(a.ownerId) && pipeMetaOf(doc.entities[a.ownerId])) {
        seen.add(a.ownerId);
        queue.push(a.ownerId);
      }
    }
    for (const e of Object.values(doc.entities)) {
      if (seen.has(e.id)) continue;
      const om = pipeMetaOf(e);
      if (om && [om.a, om.b].some((a) => a.kind === "port" && a.ownerId === id)) {
        seen.add(e.id);
        queue.push(e.id);
      }
    }
  }
  return [...seen];
}

/**
 * ONE command that redraws fitting `id` at `size` (and `size2`: tee branch, reducer small end) and, when `withPipes`,
 * gives the pipes on its ends (and their runs) the size of their end. A sentence instead when it cannot.
 */
export function resizeFittingCommand(
  doc: Doc,
  id: string,
  size: number,
  size2?: number,
  withPipes = true
): { cmd: Command; pipesChanged: number; info: FittingInfo } | string {
  const g = doc.entities[id];
  const info = fittingInfoOf(g);
  if (!g || g.type !== "group" || !info) return "Select a bend, a tee or a reducer.";
  const sizes = normalSizes(info.kind, size, size2, info);
  if (typeof sizes === "string") return sizes;
  if (sizes.size === info.size && (sizes.size2 ?? info.size2) === info.size2 && rawPorts(g)) return "The fitting already has that size.";

  const item = makeFittingItem(info.kind, sizes.size, sizes.size2);
  const ng = item.entities.find((e) => e.type === "group")!;
  const newParts = item.entities.filter((e) => e.type !== "group");
  const oldParts = g.children.map((c) => doc.entities[c]);
  if (oldParts.length !== newParts.length || oldParts.some((e, i) => !e || sig(e) !== sig(newParts[i]!))) {
    return "This fitting was changed after it was placed (ungrouped or edited). Place a new fitting of the size you need.";
  }
  const oldPorts = placedPorts(g, info);
  const newPorts = ng.meta!.ports as FittingPortData[];
  if (!oldPorts || oldPorts.length !== newPorts.length) return "This fitting has no ends that TraceCAD can read. Place a new fitting.";

  // the new fitting turned as the old one, its centre on the old centre
  const turn = ang(oldPorts[0]!.dir) - ang(newPorts[0]!.dir);
  const c0 = centreOf(info.kind, newPorts);
  const c1 = centreOf(info.kind, oldPorts);
  const mp = (p: Vec): Vec => {
    const r = rot({ x: p.x - c0.x, y: p.y - c0.y }, turn);
    return { x: c1.x + r.x, y: c1.y + r.y };
  };
  const deg = (turn * 180) / Math.PI;
  // a mirrored fitting would put its other ends on the wrong side: refuse rather than draw it wrong
  for (let i = 1; i < newPorts.length; i++) {
    const want = oldPorts[i]!.dir;
    const got = rot(newPorts[i]!.dir, turn);
    if (Math.hypot(want.x - got.x, want.y - got.y) > 1e-6) return "This fitting is mirrored. Place a new fitting of the size you need.";
  }

  const patches: Patch[] = [];
  oldParts.forEach((o, i) => {
    const n = newParts[i]!;
    if (!o) return;
    if (o.type === "polyline" && n.type === "polyline") patches.push({ id: o.id, before: { points: structuredClone(o.points) }, after: { points: n.points.map(mp) } });
    else if (o.type === "arc" && n.type === "arc")
      patches.push({ id: o.id, before: { center: { ...o.center }, r: o.r, startDeg: o.startDeg, sweepDeg: o.sweepDeg }, after: { center: mp(n.center), r: n.r, startDeg: n.startDeg + deg, sweepDeg: n.sweepDeg } });
    else if (o.type === "circle" && n.type === "circle") patches.push({ id: o.id, before: { center: { ...o.center }, r: o.r }, after: { center: mp(n.center), r: n.r } });
  });
  // the group: what it is, its ends, and its turning point (moved to the new inlet when it was on the old one)
  const oldPivot = g.meta?.pivot as Vec | undefined;
  const pivotWasInlet = !!oldPivot && Math.hypot(oldPivot.x - oldPorts[0]!.p.x, oldPivot.y - oldPorts[0]!.p.y) < 1e-6;
  const newInfo: FittingInfo = { kind: info.kind, size: sizes.size, ...(sizes.size2 !== undefined ? { size2: sizes.size2 } : {}) };
  const meta = {
    ...(g.meta ?? {}),
    fitting: newInfo,
    ports: newPorts.map((pt) => ({ p: mp(pt.p), dir: rot(pt.dir, turn), size: pt.size })),
    ...(oldPivot && !pivotWasInlet ? {} : { pivot: mp(ng.meta!.pivot as Vec) })
  };
  patches.push({ id, before: { meta: structuredClone(g.meta) }, after: { meta } });

  const commands: Command[] = [{ kind: "updateEntities", patches }];
  let pipesChanged = 0;
  if (withPipes) {
    const draft = structuredClone(doc);
    applyCommand(draft, commands[0]!);
    const done = new Set<string>();
    newPorts.forEach((pt, index) => {
      for (const e of Object.values(draft.entities)) {
        const pm = pipeMetaOf(e);
        if (!pm || done.has(e.id)) continue;
        if (![pm.a, pm.b].some((a) => a.kind === "port" && a.ownerId === id && a.port === index)) continue;
        for (const pid of runFrom(draft, e.id)) {
          if (done.has(pid)) continue;
          done.add(pid);
          const cur = pipeMetaOf(draft.entities[pid]);
          if (!cur || cur.size === pt.size) continue;
          const ps = setPipePatches(draft, pid, { size: pt.size });
          if (!ps.length) continue;
          const cmd: Command = { kind: "updateEntities", patches: ps };
          applyCommand(draft, cmd);
          commands.push(cmd);
          pipesChanged++;
        }
      }
    });
  }
  return { cmd: commands.length === 1 ? commands[0]! : { kind: "batch", label: "fitting size", commands }, pipesChanged, info: newInfo };
}
